import styled from 'styled-components';

import { colors, media } from '../../../core';

type colorsKeys = keyof typeof colors;

export interface ButtonProps {
  variant: 'primary' | 'secondary' | 'outline-reverse';
  size: 'xsmall' | 'small' | 'medium' | 'large';
  width?: string;
  backGroundColor?: (typeof colors)[colorsKeys];
}

const Button = styled.button.attrs(({ type }) => ({
  type: type || 'button',
}))<ButtonProps>`
  -webkit-appearance: none;
  border: 0;
  border-radius: 4px;
  cursor: default;
  display: inline-flex;
  font-family: ${({ theme }) => theme.fontFamily || 'Quicksand, sans-serif'};
  font-size: 16px;
  font-weight: ${({ theme }) => theme.buttonFontWeight || 600};
  line-height: 20px;
  padding: 0 12px;
  text-decoration: none;
  text-align: center;
  align-items: center;
  justify-content: center;

  &:hover {
    cursor: pointer;
  }

  &:focus {
    outline: 0;
    box-shadow: ${({ theme }) => theme.buttonFocusShadow || `0 0 3px ${colors.primaryHover}`};
  }

  &:disabled {
    cursor: default;
  }

  ${({ variant, theme }) =>
    variant === 'primary' &&
    `
    background-color: ${theme.buttonPrimaryColor || colors.primary};
    color: ${theme.buttonPrimaryTextColor || colors.white};
    transition: background-color 0.4s ease, color 0.4s ease;

    &:hover,
    &:focus {
      background-color: ${theme.buttonPrimaryHoverColor || colors.primaryHover};
      color: ${theme.buttonPrimaryHoverTextColor || colors.white};
    }

    &:disabled {
      background-color: ${theme.buttonPrimaryDisabledColor || colors.primaryDisabled};
      color: ${theme.buttonPrimaryDisabledTextColor || colors.white};
    }
  `}

  ${({ variant, theme }) =>
    variant === 'secondary' &&
    `
    border: 1px solid ${theme.buttonSecondaryBorderColor || colors.primary};
    color: ${theme.buttonSecondaryColor || colors.primary};
    transition: background-color 0.4s ease, color 0.4s ease;
    background-color: ${theme.buttonSecondaryBackgroundColor || colors.white};

    &:hover,
    &:focus {
      border-color: ${theme.buttonSecondaryHoverBorderColor || colors.primaryHover};
      color: ${theme.buttonSecondaryHoverColor || colors.primaryHover};
    }

    &:disabled {
      border-color: ${theme.buttonSecondaryDisabledColor || colors.primaryDisabled};
      color: ${theme.buttonSecondaryDisabledColor || colors.primaryDisabled};
    }
  `}

  ${({ variant }) =>
    variant === 'outline-reverse' &&
    `
    background-color: transparent;
    border: 1px solid ${colors.white};
    color: ${colors.white};
    transition: background-color 0.4s ease, color 0.4s ease, opacity: 0.4s ease;

    &:hover,
    &:focus {
      opacity: 0.9;
      border-color: ${colors.white};
      color: ${colors.white};
    }

    &:disabled {
      opacity: 0.5;
    }
  `}

  ${({ size }) =>
    size === 'xsmall' &&
    `
    font-size: 14px;
    line-height: 18px;
    min-width: 110px;
    height: 32px;
  `}

  ${({ size }) =>
    size === 'small' &&
    `
    font-size: 16px;
    line-height: 20px;
    height: 44px;
    min-width: 150px;

    ${media.tabletPortrait} {
      font-size: 14px;
    }
  `}

  ${({ size }) =>
    size === 'medium' &&
    `
    font-size: 16px;
    line-height: 20px;
    min-width: 190px;
    height: 44px;

    ${media.tabletPortrait} {
      font-size: 14px;
    }
  `}

  ${({ size }) =>
    size === 'large' &&
    `
    font-size: 16px;
    line-height: 20px;
    min-width: 300px;
    height: 54px;

    ${media.tabletPortrait} {
      font-size: 14px;
    }
  `}

  width: ${({ width }) => width};

  ${({ backGroundColor }) =>
    backGroundColor
      ? `
    background-color: ${backGroundColor}EE;

    &:hover,
    &:focus {
      background-color: ${backGroundColor};
      box-shadow: 0 0 3px ${backGroundColor};
    }
  `
      : ''}
`;

export default Button;
